/*
 * @Author: zhushitao
 * @Date: 2021-07-22 19:36:41
 * @LastEditors: zhushitao
 * @LastEditTime: 2021-11-12 19:34:34
 * @Description: core
 */
import { observe } from 'mobx';

import IChannel from './channel';
import { Lifecycle } from './constants/hooks';
import { MESSAGE_TYPE } from './constants/message';
import AppContext from './lib/appCtx';
import { Executer } from './lib/executer';
import { IMessage } from './lib/message';
import PluginProcessor from './lib/plugin';
import { Spectator } from './lib/spectator';
import {
  IAppContext,
  IBaseConfig,
  IMessageCtx,
  makeChannelFunction,
  MiddleWare,
} from './lib/type';
import { ErrorCode, IConfig, InfoCode, LOG_LEVEL } from './utils/logger';

class ChatPro<
  T extends IMessage = IMessage,
  C extends IChannel<any> = IChannel<any>,
> {
  public appContext: IAppContext<T, C>; //上下文
  private baseConfig: IBaseConfig<T>; //基础配置
  private sendExecuter: Executer<T> = new Executer<T>(); //发送消息中间件执行器
  private receiveExecuter: Executer<T> = new Executer<T>(); //消息中间件执行器
  private spectator: Spectator = new Spectator();

  /**
   * @param baseConfig: IBaseConfig<T>
   * @param appContext?: IAppContext<T, C> 自定义上下文
   */
  constructor(baseConfig: IBaseConfig<T>, appContext?: IAppContext<T, C>) {
    this.appContext = appContext || new AppContext(baseConfig.query);
    observe(this.appContext, (obj) => {
      this.appContextListener(obj.object);
    });
    this.appContext.logger = baseConfig.logger;
    this.baseConfig = baseConfig;
    this.init();
  }

  /**
   * @description SDK初始化生命周期
   * @author zhushitao
   */
  private init() {
    const baseConfig = this.baseConfig;
    const pluginProcessor = new PluginProcessor(this, this.appContext);
    // 处理config
    this.processHandler(baseConfig);
    // 插件处理
    pluginProcessor.registerPlugins(baseConfig.plugins);
  }

  private appContextListener = async (newAppContext: IAppContext<T>) => {
    try {
      await this.spectator.publish(Lifecycle.APP_CONTEXT_CHANGE, newAppContext);
    } catch (error) {
      newAppContext.logger?.error(ErrorCode.AppCtxListenError, {
        error: error,
      });
      throw error;
    }
  };

  /**
   * @description run
   */
  public async run() {
    const baseConfig = this.baseConfig;
    await this.spectator
      .publish(Lifecycle.APP_WILL_MOUNT, this.appContext)
      .then(() => {
        this.appContext.logger?.info(Lifecycle.APP_WILL_MOUNT);
      })
      .catch((error) => {
        this.appContext.logger?.error(ErrorCode.InitError, {
          error: error,
        });
        throw error;
      });

    this.appContext.appLoading = true;

    this.appContext.appStatus = Lifecycle.CREATE_DIALOG_ING;
    baseConfig
      .createDialog?.(this.appContext)
      .then((breakInitChannel?: boolean) => {
        this.spectator
          .publish(Lifecycle.CREATE_DIALOG_SUCCESS, this.appContext)
          .then(() => {
            this.appContext.appLoading = false;
          })
          .catch(() => {
            this.appContext.appLoading = false;
          });
        if (breakInitChannel) {
          this.appContext.appStatus = Lifecycle.APP_DID_MOUNT;
          return this.spectator.publish(
            Lifecycle.APP_DID_MOUNT,
            this.appContext,
          );
        }
      })
      .catch((error: any) => {
        this.appContext.appLoading = false;
        this.appContext.logger?.error(ErrorCode.InitError, {
          error: error,
        });
        throw error;
      });

    try {
      this.appContext.appStatus = Lifecycle.CHANNEL_WILL_MOUNT;

      await this.spectator.publish(
        Lifecycle.CHANNEL_WILL_MOUNT,
        this.appContext,
      );
      this.appContext.logger?.info(InfoCode.ChannelWillMount);
    } catch (error) {
      this.appContext.logger?.error(ErrorCode.ChannelWillMountError, {
        error,
      });
      throw error;
    }

    try {
      const channel = await baseConfig.makeChannel?.(this.appContext);

      this.appContext.channel = channel;
      this.appContext.logger?.info(InfoCode.ChanneInitSuccess);
      // 会话消息更新
      channel?.subscribe?.(Lifecycle.MESSAGE_CHANGE, (data: T) => {
        this.spectator.publish(Lifecycle.MESSAGE_CHANGE, this.appContext, data);
        if (data.type === MESSAGE_TYPE.CMD) {
          this.appContext.cmd?.send(data.content);
        }
      });
      // 会话更新
      channel?.subscribe?.(Lifecycle.DIALOG_CHANGE, async (data: any) => {
        try {
          this.appContext.dialog = data;
          await this.spectator.publish(
            Lifecycle.DIALOG_CHANGE,
            this.appContext,
            data,
          );
        } catch (error) {
          this.appContext.logger?.error(ErrorCode.DialogChangeError, {
            error: error,
          });
          throw error;
        }
      });

      channel?.subscribe?.(
        Lifecycle.MESSAGES_CHANGE,
        async (data: T[] = []) => {
          try {
            //过滤CMD类型的消息
            const renderList = data.filter(
              (message) => message.type !== MESSAGE_TYPE.CMD,
            );
            const messageResultList = await Promise.all(
              renderList.map((message) => this.messageCtxChannel(message)),
            );
            this.appContext.messages = messageResultList;
            await this.spectator.publish(
              Lifecycle.MESSAGES_CHANGE,
              this.appContext,
              data,
            );
          } catch (error) {
            this.appContext.logger?.error(ErrorCode.MessagesChangeError, {
              error: error,
            });
            throw error;
          }
        },
      );

      if (channel) {
        channel
          .init(this.appContext)
          .then(() => {
            // 通道初始化之后
            this.appContext.appStatus = Lifecycle.CHANNEL_DID_MOUNT;
            this.spectator
              .publish(Lifecycle.CHANNEL_DID_MOUNT, this.appContext)
              .then(async () => {
                this.appContext.logger?.info(InfoCode.ChannelDidMount);
                try {
                  this.appContext.appStatus = Lifecycle.APP_DID_MOUNT;
                  await this.spectator.publish(
                    Lifecycle.APP_DID_MOUNT,
                    this.appContext,
                  );
                  this.appContext.logger?.info(InfoCode.AppDidMount);
                } catch (error) {
                  this.appContext.logger?.error(ErrorCode.AppMountError, {
                    error: error,
                  });
                  throw error;
                }
              })
              .catch((error) => {
                this.appContext.logger?.error(ErrorCode.ChannelDidMountError, {
                  error: error,
                });
                throw error;
              });
          })
          .catch((error: unknown) => {
            this.appContext.logger?.error(ErrorCode.ChannelInitError, {
              error: error,
            });
            throw error;
          });
      }
    } catch (error) {
      this.appContext.logger?.error(ErrorCode.ChannelSubEventError, {
        error: error,
      });
      throw error;
    }
  }

  /**
   * 描述
   * @author zhushitao@bytedance.com
   * @date 2021-01-26
   * @param {string} name
   * @param {any} instance
   * @returns void
   */
  public addSingleton(name: string, instance: any) {
    (this.appContext as any)[name] = instance;
    instance.appContext = this.appContext;
  }

  processHandler(config: IBaseConfig<T>) {
    this.registerRuntime(config.layout);
    this.registerComponent(config.components);
    this.registerLifecycle(config.lifecycle);
    this.registerJsbrige(config.jsBrige);
    this.registerUtils(config.utils);
    this.connectMiddleware(config.middleware);
    this.appContext.themes = {
      ...(this.appContext.themes || {}),
      ...config.themes,
    };
    this.appContext.lang = config.lang;
    this.appContext.autoComplete = this.appContext.autoComplete
      ? Object.assign(this.appContext.autoComplete, config.autoComplete)
      : config.autoComplete;
  }

  public async destroy(data?: any) {
    try {
      await this.spectator.publish(Lifecycle.APP_DESTROY, this.appContext);
      this.appContext.channel?.destroy &&
        (await this.appContext.channel?.destroy());
    } catch (error) {
      throw error;
    }
  }

  /**
   * 描述
   * @author zhushitao@bytedance.com
   * @date 2021-01-26
   * @param message:T
   * @returns middleware compose
   */
  private async messageCtxChannel(message: T) {
    try {
      const result = await this.receiveExecuter.compose()({
        appContext: this.appContext,
        message,
      });
      return result.message;
    } catch (error) {
      this.appContext.logger?.error(ErrorCode.MiddleWareError, {
        error: error,
      });
      throw error;
    }
  }

  /**
   * @param makeChannel 返回长连接实例
   */
  private async initSocket(makeChannel?: makeChannelFunction<IAppContext<T>>) {
    if (makeChannel) {
      try {
        const channel = await makeChannel(this.appContext);
        if (channel) {
          this.appContext.channel = channel;
          this.appContext.sendMessage = async (message: any, ...otherArgs) => {
            try {
              const filter = await this.sendExecuter.compose()({
                appContext: this.appContext,
                message,
              });
              if (filter?.message && channel.sendMessage) {
                await channel.sendMessage(
                  filter.appContext,
                  filter.message,
                  ...otherArgs,
                );
              }
            } catch (error) {
              throw error;
            }
          };
          this.appContext.sendFile = async (message: any, ...otherArgs) => {
            try {
              if (message && channel?.sendFile) {
                await channel.sendFile(this.appContext, message, ...otherArgs);
              }
            } catch (error) {
              throw error;
            }
          };
        } else {
          throw new Error('未初始化找到通道的实例方法');
        }
        return channel;
      } catch (e) {
        throw e;
      }
    }
  }

  /**
   * @description 中间件注册
   * @param middleware send中间件 receive中间件
   */
  private async connectMiddleware(middleware?: {
    send: MiddleWare<T>[];
    receive: MiddleWare<T>[];
  }) {
    if (!middleware) return;
    if (middleware?.send) {
      try {
        this.sendExecuter.useSome(middleware?.send);
      } catch (error) {
        this.appContext.logger?.error(ErrorCode.MiddleWareRsError, {
          error: error,
        });
      }
    }
    if (middleware?.receive) {
      try {
        this.receiveExecuter.useSome(middleware?.receive);
      } catch (error) {
        this.appContext.logger?.error(ErrorCode.MiddleWareRsError, {
          error: error,
        });
      }
    }
  }

  /**
   * @description 注册生命周期
   * @param lifecycle
   */
  private registerLifecycle(lifecycle: Record<string, any>) {
    if (lifecycle) {
      Object.keys(lifecycle).forEach((lifeName: string) => {
        this.spectator.subscribe(lifeName, lifecycle[lifeName]);
      });
    }
  }

  /**
   * 描述
   * @author zhushitao@bytedance.com
   * @date 2021-01-26
   * @param config:ILayoutConfig
   * @returns void
   */

  private registerRuntime(config: any) {
    this.appContext.renderConfig = Object.assign(
      this.appContext.renderConfig,
      config,
    );
  }

  /**
   * 注册utils
   * @author zhushitao@bytedance.com
   * @date 2021-01-26
   * @param {any} utils
   * @returns {void}
   */
  private registerUtils(utils?: any) {
    // this.appContext.utils = utils;
    this.appContext.utils = Object.assign(this.appContext.utils, utils);
  }

  /**
   * 注册jsBrige
   * @author zhushitao@bytedance.com
   * @date 2021-01-26
   * @param {any} jsBrige
   * @returns {void}
   */
  private registerJsbrige(jsBrige?: any) {
    // this.appContext.jsBrige = jsBrige;
    this.appContext.jsBrige = Object.assign(
      {
        openWindow: window.open,
      },
      this.appContext.jsBrige,
      jsBrige,
    );
  }
  /**
   * 注册一些基本组件
   * @author zhushitao@bytedance.com
   * @date 2021-01-26
   * @param {Record<string, any>} components
   * @returns void
   */
  private registerComponent(components?: Record<string, any>) {
    if (components) {
      Object.keys(components).forEach((name: string) => {
        this.appContext.libraray.registerComponent(name, components[name]);
      });
    }
  }
}
export {
  AppContext,
  Lifecycle,
  LOG_LEVEL,
  ErrorCode,
  InfoCode,
  IChannel,
  ChatPro,
};
export { COMPONENT_TYPE, MessagePosition } from './constants/component';
export { Log } from './utils/logger';
export type {
  IAppContext,
  IMessage,
  IMessageCtx,
  IBaseConfig,
  IConfig as ILoggerConfig,
};
export default ChatPro;
